import { convertToApiQuery } from 'src/modules/seek-jobs-search-query';
import checksum from '@seek/request-checksum';

import {
  withRequestId,
  withUserAgent,
  withXRealIp,
  createUnauthenticatedHttpClient,
} from 'src/modules/seek-api-request';
import get from 'lodash/get';
import has from 'lodash/has';
import clean from 'src/modules/clean-object';
import axios from 'axios';
import {
  searchUrlV4 as jobSearchUrlV4,
  searchUrlNZV4 as jobSearchUrlNZV4,
  v4CountsUrl,
  v4RelatedSearchesUrl,
} from 'src/config';
import type { SearchParams } from '@seek/chalice-types';
import type { Zone } from '@seek/audience-zones';
import getCountry from 'src/config/utils/getCountryFromZone';
import type { TestHeaders } from 'src/utils/productionTesting/productionTesting';
import type { Brand, Locale } from '@seek/melways-sites';
import type { Country } from 'src/types/globals';
import type { SmarterSearchCluster } from 'src/config/types';

// Matches the jobsearch api timeout. Speak to the #jobsearch
// team when changing this value.
export const SEARCH_API_TIMEOUT = 3000;

interface BaseAPIProps {
  searchParams: SearchParams;
  brand?: Brand;
  country: Country;
  zone: Zone;
  cookies: Record<string, any>;
  requestId?: string;
  timeout?: number;
  testHeaders: TestHeaders;
  locale: Locale;
}

let onlySearchPromise: any,
  onlyCountsPromise: Promise<any> | undefined,
  onlyRelatedSearchPromise: Promise<any> | undefined,
  // Use AxiosType from ca-http-client once moved to ts
  onlyRelatedSearchPromiseSource: any,
  onlySearchPromiseSource: any,
  onlyCountsPromiseSource: any;

function buildApiQuery({
  searchQuery,
  country,
  zone,
  cookies,
  userQueryId,
  seekerId,
  solId,
  locale,
}: {
  searchQuery: SearchParams;
  country: Country;
  zone: Zone;
  cookies?: Record<string, any>;
  userQueryId?: string;
  seekerId?: number;
  solId?: string;
  locale: Locale;
}) {
  const analyticsParams = !cookies
    ? {}
    : {
        userqueryid: userQueryId || null,
        userid: cookies.JobseekerVisitorId || null,
        usersessionid: cookies.JobseekerVisitorId || null,
        eventCaptureSessionId: cookies.JobseekerSessionId || null,
      };

  // TODO-ZONES: See if we can update the library so we can pass in Zone
  // instead of country value
  const searchParams = convertToApiQuery({
    country,
    searchQuery,
  });

  const clusterSearchQuery = cookies?.SMARTER_SEARCH_LAUNCH_CLUSTER
    ? { ...searchParams, cluster: cookies.SMARTER_SEARCH_LAUNCH_CLUSTER }
    : searchParams;

  return clean({
    siteKey: `${getCountry(zone)}-Main`,
    sourcesystem: 'houston',
    ...analyticsParams,
    ...clusterSearchQuery,
    locale,
    seekerId,
    solId,
  });
}

const buildApiHeaders = (requestId?: string) => ({
  ...withRequestId(requestId),
});

/**
 * TODO: We need to investigate what domains will be utilised
 * in all regions and how we want to handle this logic.
 */
function getUrl({ zone }: { zone: Zone }) {
  const country = getCountry(zone);
  switch (country) {
    case 'NZ':
      return jobSearchUrlNZV4;
    case 'AU':
    default:
      return jobSearchUrlV4;
  }
}
function search({
  searchParams,
  brand,
  country,
  zone,
  cookies,
  userQueryId,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  userAgent,
  seekerId,
  solId,
  testHeaders,
  locale,
  xRealIp,
}: BaseAPIProps & {
  userQueryId: string;
  userAgent: string;
  seekerId?: number;
  solId?: string;
  xRealIp?: string;
}) {
  if (onlySearchPromise) {
    onlySearchPromiseSource.cancel();
  }
  const apiQuery = buildApiQuery({
    searchQuery: searchParams,
    country,
    zone,
    cookies,
    userQueryId,
    seekerId,
    solId,
    locale,
  });

  const searchPromise = createUnauthenticatedHttpClient({
    retryPolicy: { retries: 0 },
    defaultRequestConfig: {
      headers: testHeaders,
    },
  })
    .send({
      url: getUrl({ zone }),
      timeout,
      params: apiQuery,
      label: 'search-experience-api',
      cancelToken: get(onlySearchPromiseSource, 'token'),
      headers: {
        ...buildApiHeaders(requestId),
        ...withUserAgent(userAgent),
        ...withXRealIp(xRealIp),
        'x-seek-checksum': checksum(apiQuery),
        'seek-request-brand': brand,
        'seek-request-country': country,
      },
    })
    .then((response: Record<string, any>) => {
      const result = get(response, 'data');

      if (has(result, 'data')) {
        return result;
      }

      throw new Error(`invalid api response: ${JSON.stringify(result)}`);
    });

  if (ENV.CLIENT) {
    onlySearchPromise = searchPromise;
    onlySearchPromiseSource = axios.CancelToken.source();
  }

  return searchPromise;
}

function counts({
  searchParams,
  country,
  zone,
  cookies,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  testHeaders,
  locale,
}: BaseAPIProps) {
  if (onlyCountsPromise) {
    onlyCountsPromiseSource.cancel();
  }

  const countsPromise = Promise.resolve(
    createUnauthenticatedHttpClient({
      omitXSeekSiteHeader: true,
      defaultRequestConfig: {
        headers: testHeaders,
      },
    }).send({
      url: v4CountsUrl,
      timeout,
      cancelToken: get(onlySearchPromiseSource, 'token'),
      label: 'search-counts',
      params: buildApiQuery({
        searchQuery: searchParams,
        zone,
        cookies,
        country,
        locale,
      }),
      headers: buildApiHeaders(requestId),
    }),
  ).then((response) => response.data);

  if (ENV.CLIENT) {
    onlyCountsPromise = countsPromise;
    onlyCountsPromiseSource = axios.CancelToken.source();
  }

  return countsPromise;
}

function relatedSearches({
  zone,
  keywords,
  where,
  requestId,
  timeout,
  testHeaders,
  countryCode,
  cluster,
  visitorId,
}: {
  countryCode: string;
  zone: string;
  keywords: string;
  where?: string;
  requestId?: string;
  timeout: number;
  testHeaders: TestHeaders;
  cluster: SmarterSearchCluster;
  visitorId: string;
}): Promise<any> {
  if (onlyRelatedSearchPromise) {
    onlyRelatedSearchPromiseSource.cancel();
  }

  const relatedSearchPromise = Promise.resolve(
    createUnauthenticatedHttpClient({
      omitXSeekSiteHeader: true,
      defaultRequestConfig: {
        headers: testHeaders,
      },
    }).send({
      url: v4RelatedSearchesUrl,
      timeout,
      cancelToken: get(onlySearchPromiseSource, 'token'),
      label: 'related-search',
      params: {
        zone,
        keywords,
        siteKey: countryCode,
        cluster,
        where,
        solId: visitorId,
      },
      headers: withRequestId(requestId),
    }),
  ).then(({ data }) => data);

  if (ENV.CLIENT) {
    onlyRelatedSearchPromise = relatedSearchPromise;
    onlyRelatedSearchPromiseSource = axios.CancelToken.source();
  }

  return relatedSearchPromise;
}

export default {
  search,
  counts,
  relatedSearches,
};
